import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { ToastService } from '@services/toast.service';
import { ConfirmDialog } from '@shared/components/confirm-dialog/confirm-dialog';
import { AuthService } from '@services/auth.service';
import { JwtService } from '@services/jwt.service';
import { StorageKey, StorageService } from '@services/storage.service';
import { CommonService } from '@services/common.service';
import { PermissionService } from '@services/permission.service';
import { APP_PATHS, Endpoints } from '@/app/utils';
import { MyProfile } from '@/app/pages/my-profile/my-profile';
import { ChangePassword } from '@/app/authentication/change-password/change-password';
import { MenuRightsNode } from '@/app/core/models/role.model';
import { environment } from '@/environments/environment';

interface MenuItem {
  menuName: string;
  menuUrl: string;
  menuIcon: string;
}

/** Matches header.scss mobile breakpoint where nav stacks vertically. */
const MOBILE_NAV_MAX_WIDTH = 768;
const HIDDEN_NAV_MENUS: string[] = [APP_PATHS.STUDY];

@Component({
  selector: 'app-header',
  imports: [RouterLink, NgTemplateOutlet, MyProfile, ChangePassword],
  templateUrl: './header.html',
  styleUrl: './header.scss'
})
export class Header implements OnInit, AfterViewInit {
  private readonly authService = inject(AuthService);
  private readonly jwtService = inject(JwtService);
  private readonly commonService = inject(CommonService);
  private readonly storageService = inject(StorageService);
  private readonly permService = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  private readonly navEl = viewChild<ElementRef<HTMLElement>>('navEl');
  private readonly measureEl = viewChild<ElementRef<HTMLElement>>('measureEl');
  private readonly moreMeasureEl = viewChild<ElementRef<HTMLElement>>('moreMeasureEl');

  private resizeObserver: ResizeObserver | null = null;
  private overflowFrame = 0;

  protected readonly appPaths = APP_PATHS;

  protected readonly isHeaderReady = signal(true);
  protected readonly isCollapsed = signal(true);
  protected readonly dropdownOpen = signal(false);
  protected readonly moreOpen = signal(false);
  protected readonly showProfileModal = signal(false);
  protected readonly showChangePasswordModal = signal(false);
  protected readonly changePasswordOtpOpen = signal(false);

  protected readonly currentUserId = signal<string>('');
  protected readonly currentUser = computed(() => this.authService.currentUser());
  protected readonly currentUserName = computed(() => this.currentUser()?.fullName || 'Admin User');

  protected readonly currentRoleName = computed(() => this.currentUser()?.roleName || 'Administrator');
  protected readonly currentStudyName = signal('');
  protected readonly profilePath = computed(() => this.currentUser()?.profilePicture || '/assets/images/faceid.png');
  protected readonly roleId = signal('1');
  protected readonly uniqueStudyId = signal<string | null>(null);
  protected readonly currentUrl = signal(this.router.url);
  /** Menu URL the current page belongs to — differs from currentUrl for sub-pages (see resolveActiveSection). */
  protected readonly activeSection = signal(this.resolveActiveSection(this.router.url, null));
  protected readonly isMobileNav = signal(false);

  protected readonly menus = signal<MenuItem[]>([]);
  protected readonly isSuperAdmin = signal(false);
  private readonly visibleCount = signal(0);

  protected readonly isStudyHeader = computed(() => {
    const sId = this.uniqueStudyId();
    const url = this.currentUrl();
    return (
      !sId ||
      sId === 'null' ||
      url.startsWith('/study') ||
      url.startsWith('/users') ||
      url.startsWith('/add-edit-study')
    );
  });

  protected readonly hasSelectedStudy = computed(() => {
    const path = this.currentUrl().split(/[?#]/)[0];
    if (path === '/study' || path === '/add-edit-study') {
      return false;
    }
    const sId = this.uniqueStudyId();
    return !!sId && sId !== 'null';
  });

  protected readonly primaryMenu = computed(() => {
    const all = this.menus();
    if (this.isMobileNav()) {
      return all;
    }
    return all.slice(0, this.visibleCount());
  });

  protected readonly moreMenu = computed(() => {
    const all = this.menus();
    if (this.isMobileNav()) {
      return [];
    }
    return all.slice(this.visibleCount());
  });

  protected readonly isMoreActive = computed(() =>
    this.moreMenu().some((item) => this.activeSection().startsWith(item.menuUrl))
  );

  constructor() {
    afterNextRender(() => {
      this.updateMobileNav();
      this.setupOverflowObserver();
      this.scheduleOverflowRecalc();
    });

    effect(() => {
      const studyHeader = this.isStudyHeader();
      const count = this.menus().length;
      untracked(() => {
        if (!studyHeader && count > 0) {
          queueMicrotask(() => {
            this.setupOverflowObserver();
            this.scheduleOverflowRecalc();
          });
        }
      });
    });
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.activeSection.set(this.resolveActiveSection(event.urlAfterRedirects, this.activeSection()));
        this.isCollapsed.set(true);
        this.moreOpen.set(false);
        this.dropdownOpen.set(false);
        this.syncState();
        this.scheduleOverflowRecalc();
      });

    this.syncState();
    this.loadMenus();
    this.authService.fetchUserProfile().subscribe();
  }

  ngAfterViewInit(): void {
    this.scheduleOverflowRecalc();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateMobileNav();
    this.scheduleOverflowRecalc();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.app-nav__more')) {
      this.moreOpen.set(false);
    }
    if (!target?.closest('.user-menu')) {
      this.dropdownOpen.set(false);
    }
  }

  private loadMenus(): void {
    const isSuperAdmin = this.jwtService.isSuperAdmin();
    const roleId = this.jwtService.getRoleId();
    if (!roleId) {
      this.permService.loadEmpty();
      this.menus.set([]);
      this.visibleCount.set(0);
      return;
    }

    const endpoint = Endpoints.menuRights.getRoleRightsByRoleId.replace('{uniqueRoleId}', roleId);
    this.commonService.doGet<MenuRightsNode[]>(endpoint).subscribe({
      next: (response) => {
        if (!response.success) {
          this.permService.loadEmpty();
          this.menus.set([]);
          this.visibleCount.set(0);
          return;
        }

        const nodes = response.data ?? [];

        // Populate the PermissionService cache (used by MenuRightGuard + pages)
        this.permService.load(nodes, isSuperAdmin);

        const items = nodes
          .filter((m) => !!m.isView && !!m.menuUrl && !HIDDEN_NAV_MENUS.includes(m.menuUrl.replace(/^\/?/, '/').toLowerCase()))
          .map((m) => this.toMenuItem(m));

        this.menus.set(items);
        this.visibleCount.set(items.length);
        this.scheduleOverflowRecalc();
      },
      error: () => {
        this.permService.loadEmpty();
        this.menus.set([]);
        this.visibleCount.set(0);
      }
    });
  }

  private updateMobileNav(): void {
    this.isMobileNav.set(window.innerWidth <= MOBILE_NAV_MAX_WIDTH);
  }

  private setupOverflowObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const nav = this.navEl()?.nativeElement;
    if (!nav) {
      return;
    }

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleOverflowRecalc());
      this.destroyRef.onDestroy(() => {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        cancelAnimationFrame(this.overflowFrame);
      });
    } else {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver.observe(nav);
  }

  private scheduleOverflowRecalc(): void {
    cancelAnimationFrame(this.overflowFrame);
    this.overflowFrame = requestAnimationFrame(() => {
      this.overflowFrame = requestAnimationFrame(() => this.recalculateOverflow());
    });
  }

  /** Icons can change measured widths after load. */
  onMeasureImageLoad(): void {
    this.scheduleOverflowRecalc();
  }

  private recalculateOverflow(): void {
    if (this.isStudyHeader() || this.isMobileNav()) {
      this.visibleCount.set(this.menus().length);
      return;
    }

    const nav = this.navEl()?.nativeElement;
    const measure = this.measureEl()?.nativeElement;
    const moreMeasure = this.moreMeasureEl()?.nativeElement;
    const items = this.menus();

    if (!nav || !measure || !items.length) {
      this.visibleCount.set(items.length);
      return;
    }

    const itemEls = Array.from(measure.querySelectorAll<HTMLElement>('[data-menu-measure]'));
    if (itemEls.length !== items.length) {
      this.visibleCount.set(items.length);
      return;
    }

    const available = nav.clientWidth;
    const itemWidths = itemEls.map((el) => el.getBoundingClientRect().width);
    const gap = this.readGap(measure);
    const totalWidth =
      itemWidths.reduce((sum, w) => sum + w, 0) + gap * Math.max(itemWidths.length - 1, 0);

    if (totalWidth <= available) {
      this.visibleCount.set(items.length);
      return;
    }

    const moreWidth = moreMeasure?.getBoundingClientRect().width ?? 88;
    let used = 0;
    let count = 0;

    for (let i = 0; i < itemWidths.length; i++) {
      const next = used + (count > 0 ? gap : 0) + itemWidths[i];
      const withMore = next + gap + moreWidth;
      if (withMore <= available) {
        used = next;
        count++;
      } else {
        break;
      }
    }

    this.visibleCount.set(count);
    if (this.moreOpen() && count >= items.length) {
      this.moreOpen.set(false);
    }
  }

  private readGap(measure: HTMLElement): number {
    const style = getComputedStyle(measure);
    const gap = Number.parseFloat(style.columnGap || style.gap || '0');
    return Number.isFinite(gap) ? gap : 0;
  }

  private toMenuItem(node: MenuRightsNode): MenuItem {
    return {
      menuName: node.menuName ?? '',
      menuUrl: node.menuUrl ?? '',
      menuIcon: this.resolveMenuIcon(node.menuIcon)
    };
  }

  private resolveMenuIcon(icon: string | null | undefined): string {
    if (!icon) {
      return '';
    }
    if (/^https?:\/\//i.test(icon)) {
      return icon;
    }
    const origin = (environment.apiOrigin || environment.baseUrl.replace(/\/api\/admin\/?$/i, '')).replace(/\/+$/, '');
    return `${origin}${icon.startsWith('/') ? icon : `/${icon}`}`;
  }

  private syncState(): void {
    this.roleId.set(this.jwtService.getRoleId() || '1');
    this.currentUserId.set(this.jwtService.getUserId() || '');

    const sId = this.storageService.getValue(StorageKey.uniqueStudyId);
    this.uniqueStudyId.set(sId);

    const studyName = this.storageService.getValue(StorageKey.studyName);
    if (studyName) {
      this.currentStudyName.set(studyName);
    }

    this.isSuperAdmin.set(this.jwtService.isSuperAdmin());
  }

  toggleCollapse(): void {
    this.isCollapsed.update((v) => !v);
    this.moreOpen.set(false);
    this.dropdownOpen.set(false);
  }

  toggleMore(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.moreOpen.update((v) => !v);
    this.dropdownOpen.set(false);
  }

  closeMore(): void {
    this.moreOpen.set(false);
  }

  toggleDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropdownOpen.update((v) => !v);
    this.moreOpen.set(false);
  }

  isActive(url: string): boolean {
    return this.activeSection().startsWith(url);
  }

  /**
   * Maps a URL to the menu it belongs to. Sub-pages whose path doesn't start with their menu's URL
   * (e.g. `/participant-details` under Participants) would otherwise leave no menu highlighted.
   */
  private resolveActiveSection(url: string, previous: string | null): string {
    const tree = this.router.parseUrl(url);
    const path = '/' + (tree.root.children['primary']?.segments.map((s) => s.path).join('/') ?? '');

    if (path.startsWith('/participant-details')) {
      // Opened from the Visits list carries returnTo=visits (same flag its Back link uses)
      return tree.queryParams['returnTo'] === 'visits' ? '/visits' : '/participants';
    }
    if (path.startsWith('/participant-visit-form')) {
      // Reached from Visits or Participants; no flag in the URL, so stay in whichever section led here
      return previous === '/visits' ? '/visits' : '/participants';
    }
    if (path.startsWith('/add-edit-clinic')) {
      return '/clinics';
    }
    return path;
  }

  isStudyNavActive(): boolean {
    const url = this.currentUrl();
    return url.startsWith('/study') || url.startsWith('/add-edit-study') || url === '/' || url === '';
  }

  setDefaultImage(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.src = '/assets/images/faceid.png';
  }

  openProfile(): void {
    this.dropdownOpen.set(false);
    this.showProfileModal.set(true);
  }

  closeProfileModal(): void {
    this.showProfileModal.set(false);
  }

  updateHeaderName(): void {
    this.syncState();
  }

  switchStudy(): void {
    this.dropdownOpen.set(false);
    this.storageService.setValue(StorageKey.uniqueStudyId, null);
    this.storageService.setValue(StorageKey.studyName, null);
    this.uniqueStudyId.set(null);
    this.router.navigate(['/study']);
  }

  openChangePassword(): void {
    this.dropdownOpen.set(false);
    this.showChangePasswordModal.set(true);
  }

  closeChangePasswordModal(): void {
    this.showChangePasswordModal.set(false);
    this.changePasswordOtpOpen.set(false);
  }

  logout(): void {
    this.dropdownOpen.set(false);
    void ConfirmDialog.open(this.dialog, {
      title: 'Log out?',
      message: 'You will be signed out of your account and need to log in again to continue.',
      icon: 'logout',
      confirmText: 'Log out',
      cancelText: 'Cancel',
      confirmTone: 'danger'
    })
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.performLogout();
      });
  }

  private performLogout(): void {
    this.authService.logout();
    this.toast.success('Logged out successfully.');
  }
}
